import React, { useState } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import ApprovalCard from './ApprovalCard'
import AddIcon from '@mui/icons-material/Add'
import AddNewApprovalRouteModal from './AddNewApprovalRouteModal'

import { deleteApprovalRoute, getApprovalRouteByStatus } from 'services/api/approval-routing'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import AdxButton from 'components/custom/AdxButton'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { toast } from 'react-toastify'

const ApprovalRouting = () => {
  const [showAddNewApprovalRouteModal, setShowAddNewApprovalRouteModal] = useState(false)
  const [selectedCardId, setSelectedCardId] = useState(null)
  const [selectedCard, setSelectedCard] = useState({})
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [blockedDelete, setBlockedDelete] = useState(null)
  const queryClient = useQueryClient()

  const handleOpenAddNewApprovalRouteModal = () => {
    setShowAddNewApprovalRouteModal(true)
  }

  const handleCloseAddNewApprovalRouteModal = () => {
    setShowAddNewApprovalRouteModal(false)
    setSelectedCardId(null)
    setSelectedCard({})
  }

  const handleClickViewRouteApprovalCard = (item) => {
    setSelectedCardId(item.id)
    setSelectedCard(item)
    handleOpenAddNewApprovalRouteModal()
  }

  const allApprovalRouteList = useQuery('allApprovalRouteList', async () => {
    const { data } = await getApprovalRouteByStatus()
    return data
  })

  const deleteMutation = useMutation((id) => deleteApprovalRoute(id), {
    onSuccess: () => {
      setDeleteTarget(null)
      toast.success('Approval flow deleted.')
      queryClient.invalidateQueries('allApprovalRouteList')
    },
    onError: (error) => {
      setDeleteTarget(null)
      const usage = error?.data?.data
      if (error?.status === 409 && usage) {
        setBlockedDelete({ usage })
        return
      }
      toast.error(error?.data?.message || 'Unable to delete approval flow.')
    },
  })

  const handleDeleteClick = (item) => {
    if (item?.is_used) {
      setBlockedDelete({ route: item, usage: item.usage })
      return
    }

    setDeleteTarget(item)
  }

  const routes = allApprovalRouteList?.data || []
  const usageItems = blockedDelete?.usage?.items || {}

  return (
    <Box px={{ xs: 2, lg: 0 }}>
      <Box
        bgcolor="white.main"
        p={2}
        mb={2}
        sx={{ border: 1, borderRadius: '4.5px', borderColor: 'gray-line.main' }}
      >
        <Typography textAlign="justify" color="light-gray-text.main" variant="body1">
          If you need to choose who can approve requests, manage approver groups in the "Admin
          Permissions" tab first.{' '}
          <Typography
            onClick={() => {
              window.location.href = '/app/management-centre/company-management/admin-permissions'
            }}
            as="span"
            color="alternate.main"
            sx={{ cursor: 'pointer' }}
          >
            Manage approver groups
          </Typography>
        </Typography>
      </Box>
      <Typography variant="h4" mb={0} fontWeight="bold">
        Approval Chain
      </Typography>
      <Typography variant="body1" mb={2} color="light-gray-text.main">
        Build reusable approval steps, then assign them to leave policies or claim amount rules.
      </Typography>
      <Box
        bgcolor="white.main"
        sx={{ border: 1, borderRadius: '4.5px', borderColor: 'gray-line.main' }}
      >
        {allApprovalRouteList.isLoading ? (
          <ApprovalCard />
        ) : routes.length < 1 ? (
          <Box p={3}>
            <Typography color="light-gray-text.main">No approval flows found.</Typography>
          </Box>
        ) : (
          routes.map((item) => (
            <ApprovalCard
              key={item.id}
              item={item}
              handleOpenAddNewApprovalRouteModal={() => handleClickViewRouteApprovalCard(item)}
              onDelete={handleDeleteClick}
            />
          ))
        )}
        <Box p={0.5}>
          <AdxButton
            variant={'ghost'}
            sx={{
              pl: 2,
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
            onClick={() => handleOpenAddNewApprovalRouteModal()}
          >
            <AddIcon color="alternate.main" />
            <Typography fontWeight={500} color="alternate.main" ml={1.25}>
              Add new approval flow
            </Typography>
          </AdxButton>
        </Box>
      </Box>

      <AdxInfoModal
        variant="error"
        title="Delete approval flow?"
        subtitle={
          deleteTarget
            ? `${deleteTarget.name} is not assigned to any active policy. It will be removed from this list, but history remains available.`
            : ''
        }
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        muiIcon="delete"
        iconColor="error.main"
        footerButtons
        firstBtnLabel="Delete flow"
        firstBtnLoading={deleteMutation.isLoading}
        firstBtnHandle={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        secondBtnLabel="Cancel"
        secondBtnHandle={() => setDeleteTarget(null)}
      />

      <AdxInfoModal
        variant="warning"
        title="This approval flow is in use"
        subtitle="Remove it from the assigned policies or claim rules before deleting."
        open={Boolean(blockedDelete)}
        onClose={() => setBlockedDelete(null)}
        muiIcon="info"
        iconColor="warning.main"
        footerButtons
        firstBtnLabel="Close"
        firstBtnHandle={() => setBlockedDelete(null)}
      >
        <Box width="100%">
          {(usageItems.leave_policies || []).map((item) => (
            <Typography key={`leave-${item.id}`} variant="body2" color="light-gray-text.main">
              Leave policy: {item.name}
            </Typography>
          ))}
          {(usageItems.claim_routes || []).map((item) => (
            <Typography key={`claim-${item.id}`} variant="body2" color="light-gray-text.main">
              Claim rule: {item.name}
            </Typography>
          ))}
          {(usageItems.system_references || []).map((item) => (
            <Typography key={`system-${item.id}`} variant="body2" color="light-gray-text.main">
              System: {item.name}
            </Typography>
          ))}
        </Box>
      </AdxInfoModal>

      <AddNewApprovalRouteModal
        showAddNewApprovalRouteModal={showAddNewApprovalRouteModal}
        handleCloseAddNewApprovalRouteModal={handleCloseAddNewApprovalRouteModal}
        selectedCardId={selectedCardId}
        selectedCard={selectedCard}
      />
    </Box>
  )
}

export default ApprovalRouting



